import {
  CSAT_RATING_LABELS,
  PermissionCode,
  TicketStatus,
  type TicketDetail,
} from '@gb-ticket/shared-types';
import { Loader2Icon, StarIcon } from 'lucide-react';
import { toast } from 'sonner';

import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { CsatSurveyForm } from '@/products/helpdesk/features/csat/csat-survey-form';
import { useCsatTicketSurvey, useRespondCsat } from '@/products/helpdesk/features/csat/use-csat';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import { ApiRequestError } from '@/lib/api';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { formatDateTime } from '@/lib/format';

export function TicketCsatPanel({ ticket }: { ticket: TicketDetail }) {
  const me = useMe();
  const copy = useVoiceCopy();
  const canSubmit = hasPermission(me.data, PermissionCode.CSAT_SUBMIT);
  const canRead = hasPermission(me.data, PermissionCode.CSAT_READ);
  const isRequester = me.data?.id === ticket.requester.id;
  const enabled =
    ticket.status === TicketStatus.CLOSED &&
    (isRequester || canRead || hasPermission(me.data, PermissionCode.TICKETS_READ_ALL));
  const query = useCsatTicketSurvey(ticket.id, enabled);
  const respond = useRespondCsat(ticket.id);
  const survey = query.data?.survey ?? null;

  if (!enabled || (!query.isPending && !survey)) {
    return null;
  }

  async function onSubmit(input: { rating: number; comment?: string }) {
    if (!survey) {
      return;
    }
    try {
      await respond.mutateAsync({ id: survey.id, body: input });
      toast.success(copy('helpdesk.csat.thanks'));
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Bewertung konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <TicketSectionCard
      tone={survey?.respondedAt ? 'emerald' : 'amber'}
      icon={StarIcon}
      collapsible
      defaultOpen={Boolean(survey?.canRespond && !survey.respondedAt)}
      key={survey?.canRespond && !survey.respondedAt ? 'respond' : 'done'}
      title="Zufriedenheit"
      description={
        survey?.respondedAt
          ? 'Bewertung zu diesem geschlossenen Ticket.'
          : 'Eine kurze Bewertung hilft uns, den Support zu verbessern.'
      }
    >
      {query.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Umfrage wird geladen …
        </p>
      )}
      {survey?.respondedAt && survey.rating != null && (
        <div className="space-y-1 text-sm">
          <p className="font-medium">
            {survey.rating} {survey.rating === 1 ? 'Stern' : 'Sterne'} ·{' '}
            {CSAT_RATING_LABELS[survey.rating]}
          </p>
          {survey.comment && <p className="text-muted-foreground">{survey.comment}</p>}
          <p className="text-muted-foreground">{formatDateTime(survey.respondedAt)}</p>
        </div>
      )}
      {survey?.canRespond && canSubmit && (
        <CsatSurveyForm pending={respond.isPending} onSubmit={(input) => void onSubmit(input)} />
      )}
      {survey && !survey.respondedAt && !survey.canRespond && (
        <p className="text-sm text-muted-foreground">
          Die Umfrage wurde versendet und wartet auf eine Antwort.
        </p>
      )}
    </TicketSectionCard>
  );
}
